import React, { useState, useEffect } from 'react';
import { listUsers, approveUser, updateUser, inviteUser, resendInvite, resetPassword, setUserRole } from '../../api/users';
import { listTenants } from '../../api/tenants';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Block as BlockIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  MarkEmailUnread as ResendIcon,
  LockReset as ResetPasswordIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useMobileLayout } from '../../utils/useMobileLayout';
import ResponsiveList from '../../components/common/ResponsiveList';

// Roles a caller may assign, by their own tier (mirrors the backend ceiling
// in routers/auth/users.set_user_role). FPA_ADMIN is capped at FPA_USER ↔
// FPA_ADMIN; the server re-enforces this regardless of the UI.
const ROLE_CEILING = {
  super: ['SUPER_ADMIN', 'ADMIN', 'FPA_ADMIN', 'STAFF', 'DATA_LOGGER', 'MEDIA_MANAGEMENT', 'FPA_USER'],
  admin: ['ADMIN', 'FPA_ADMIN', 'STAFF', 'DATA_LOGGER', 'MEDIA_MANAGEMENT', 'FPA_USER'],
  fpa_admin: ['FPA_USER', 'FPA_ADMIN'],
};

const UserManagement = () => {
  const { currentUser, isSuperAdmin, isAdmin, isFPAAdmin } = useAuth();
  const { isMobile } = useMobileLayout();
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogAction, setDialogAction] = useState('');
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState('');

  // Promote/demote (set role) dialog state.
  const [roleDialogUser, setRoleDialogUser] = useState(null);
  const [roleDialogValue, setRoleDialogValue] = useState('');
  const [roleSaving, setRoleSaving] = useState(false);

  // Roles the current admin may assign (super > admin > fpa_admin).
  const manageableRoles = isSuperAdmin()
    ? ROLE_CEILING.super
    : isAdmin()
      ? ROLE_CEILING.admin
      : isFPAAdmin()
        ? ROLE_CEILING.fpa_admin
        : [];

  const currentTierRole = (user) => {
    const names = (Array.isArray(user.roles) ? user.roles : []).map((r) => String(r).toUpperCase());
    return manageableRoles.find((r) => names.includes(r)) || '';
  };

  const openRoleDialog = (user) => {
    setRoleDialogValue(currentTierRole(user) || manageableRoles[0] || '');
    setRoleDialogUser(user);
  };

  const handleSaveRole = async () => {
    if (!roleDialogUser || !roleDialogValue) return;
    setRoleSaving(true);
    try {
      const updated = await setUserRole(roleDialogUser.id, roleDialogValue);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setRoleDialogUser(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to update role.');
    } finally {
      setRoleSaving(false);
    }
  };

  // Invite user state
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', username: '', role: 'public', tenant_id: '' });
  const [inviteError, setInviteError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  // Resend invite state
  const [resendingUserId, setResendingUserId] = useState(null);
  const [resendSuccess, setResendSuccess] = useState('');

  // Admin password-reset state
  const [resettingUserId, setResettingUserId] = useState(null);
  const [resetConfirmUser, setResetConfirmUser] = useState(null);

  // Fetch users and tenants
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch users
        const usersData = await listUsers();
        setUsers(usersData.users);

        // Fetch tenants (for Super Admin to edit user tenants)
        if (isSuperAdmin()) {
          try {
            const tenantsData = await listTenants();
            setTenants(tenantsData.tenants || []);
          } catch (tenantError) {
            console.error('Error fetching tenants:', tenantError);
            // Don't fail the whole component if tenants can't be fetched
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);

  const handleApprove = (user) => {
    setSelectedUser(user);
    setDialogAction('approve');
    setOpenDialog(true);
  };

  const handleUnblock = (user) => {
    setSelectedUser(user);
    setDialogAction('unblock');
    setOpenDialog(true);
  };

  const handleBlock = (user) => {
    setSelectedUser(user);
    setDialogAction('block');
    setOpenDialog(true);
  };

  const handleConfirmAction = async () => {
    setIsLoading(true);

    try {
      if (dialogAction === 'approve') {
        await approveUser(selectedUser.id);
        // Update user in the list
        setUsers(users.map(user =>
          user.id === selectedUser.id
            ? { ...user, is_approved: true }
            : user
        ));
      } else if (dialogAction === 'block') {
        await updateUser(selectedUser.id, { is_active: false });
        // Update user in the list
        setUsers(users.map(user =>
          user.id === selectedUser.id
            ? { ...user, is_active: false }
            : user
        ));
      } else if (dialogAction === 'unblock') {
        await updateUser(selectedUser.id, { is_active: true });
        // Update user in the list
        setUsers(users.map(user =>
          user.id === selectedUser.id
            ? { ...user, is_active: true }
            : user
        ));
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Failed to update user. Please try again later.');
    } finally {
      setIsLoading(false);
      setOpenDialog(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleEditTenant = (user) => {
    setEditingUser(user);
    setSelectedTenantId(user.tenant_id || '');
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingUser(null);
    setSelectedTenantId('');
  };

  const handleSaveTenantChange = async () => {
    if (!editingUser) return;

    setIsLoading(true);
    try {
      await updateUser(editingUser.id, {
        tenant_id: selectedTenantId || null
      });

      // Update user in the list
      const selectedTenant = tenants.find(t => t.id === parseInt(selectedTenantId));
      setUsers(users.map(user => 
        user.id === editingUser.id 
          ? { 
              ...user, 
              tenant_id: selectedTenantId || null,
              tenant_name: selectedTenant ? selectedTenant.name : null
            } 
          : user
      ));

      setOpenEditDialog(false);
      setEditingUser(null);
      setSelectedTenantId('');
    } catch (error) {
      console.error('Error updating user tenant:', error);
      setError('Failed to update user tenant. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };


  const handleResendInvite = async (user) => {
    setResendingUserId(user.id);
    setResendSuccess('');
    setError(null);
    try {
      await resendInvite(user.id);
      setResendSuccess(`Invite email resent to ${user.email}`);
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to resend invite to ${user.email}`);
    } finally {
      setResendingUserId(null);
    }
  };

  const handleResetPasswordConfirm = async () => {
    const user = resetConfirmUser;
    if (!user) return;
    setResetConfirmUser(null);
    setResettingUserId(user.id);
    setResendSuccess('');
    setError(null);
    try {
      await resetPassword(user.id);
      setResendSuccess(
        `A password-reset link has been emailed to ${user.email}. ` +
        `It's valid for 60 minutes — they click it to choose a new password. ` +
        `Ask them to check their spam / junk folder.`
      );
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to trigger password reset for ${user.email}`);
    } finally {
      setResettingUserId(null);
    }
  };

  const handleInviteSubmit = async () => {
    setInviteError('');
    if (!inviteData.email.trim()) { setInviteError('Email is required'); return; }
    if (!inviteData.username.trim()) { setInviteError('Username is required'); return; }

    setInviteLoading(true);
    try {
      const payload = {
        email: inviteData.email,
        username: inviteData.username,
        // Backend expects role_names (a list); the server re-enforces the
        // caller's role ceiling regardless of what's sent.
        role_names: inviteData.role ? [inviteData.role] : [],
        tenant_id: inviteData.tenant_id !== '' ? parseInt(inviteData.tenant_id) : null,
      };
      const newUser = await inviteUser(payload);
      setUsers([newUser, ...users]);
      setOpenInviteDialog(false);
      setInviteData({ email: '', username: '', role: 'FPA_USER', tenant_id: '' });
      setResendSuccess(
        `Invitation sent to ${inviteData.email}. ` +
        `Ask them to check their spam / junk folder — the email comes from noreply@fpaportal.co.za.`
      );
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Failed to invite user');
    } finally {
      setInviteLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            User Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setInviteData({ email: '', username: '', role: 'FPA_USER', tenant_id: '' });
              setInviteError('');
              setOpenInviteDialog(true);
            }}
          >
            Invite User
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {resendSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResendSuccess('')}>{resendSuccess}</Alert>}

        {(() => {
          const renderRoles = (user) => {
            const userRoles = Array.isArray(user.roles) && user.roles.length
              ? user.roles
              : (user.role ? [user.role] : []);
            if (userRoles.length === 0) {
              return <Chip label="No Role" color="default" size="small" />;
            }
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {userRoles.map((role) => {
                  const upper = String(role).toUpperCase();
                  const color =
                    upper === 'SUPER_ADMIN' ? 'error' :
                    upper === 'ADMIN'       ? 'secondary' :
                    upper === 'STAFF'       ? 'primary' :
                    'default';
                  return <Chip key={role} label={role} color={color} size="small" />;
                })}
              </Box>
            );
          };

          const renderActions = (user) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {!user.is_approved && (
                <Button size="small" color="success" startIcon={<ApproveIcon />} onClick={() => handleApprove(user)}>
                  Approve
                </Button>
              )}
              <Button
                size="small"
                color="info"
                startIcon={resendingUserId === user.id ? <CircularProgress size={14} /> : <ResendIcon />}
                onClick={() => handleResendInvite(user)}
                disabled={resendingUserId === user.id}
                title="Re-send the branded invitation email (a link to set their password)"
              >
                Resend
              </Button>
              <Button
                size="small"
                color="warning"
                startIcon={resettingUserId === user.id ? <CircularProgress size={14} /> : <ResetPasswordIcon />}
                onClick={() => setResetConfirmUser(user)}
                disabled={resettingUserId === user.id}
                title="Email this user a password-reset link so they can choose a new password."
              >
                Reset password
              </Button>
              {manageableRoles.length > 0 && user.id !== currentUser?.id && (
                <Button size="small" color="primary" startIcon={<EditIcon />} onClick={() => openRoleDialog(user)}>
                  Role
                </Button>
              )}
              {isSuperAdmin() && (
                <Button size="small" color="primary" startIcon={<EditIcon />} onClick={() => handleEditTenant(user)}>
                  Tenant
                </Button>
              )}
              {user.is_active ? (
                <Button
                  size="small"
                  color="error"
                  startIcon={<BlockIcon />}
                  onClick={() => handleBlock(user)}
                  disabled={user.id === 1}
                >
                  Block
                </Button>
              ) : (
                <Button size="small" color="success" startIcon={<ApproveIcon />} onClick={() => handleUnblock(user)}>
                  Unblock
                </Button>
              )}
            </Box>
          );

          return (
            <ResponsiveList
              items={users}
              empty="No users found."
              getKey={(u) => u.id}
              columns={[
                { key: 'username', header: 'Username', render: (u) => u.username },
                { key: 'email', header: 'Email', render: (u) => u.email },
                { key: 'role', header: 'Role', render: renderRoles },
                {
                  key: 'tenant',
                  header: 'Tenant',
                  render: (u) => (
                    <Chip label={u.tenant_name || 'No Tenant'} color={u.tenant_id ? 'info' : 'default'} size="small" />
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (u) => u.is_approved
                    ? <Chip label="Approved" color="success" size="small" />
                    : <Chip label="Pending" color="warning" size="small" />,
                },
                { key: 'created', header: 'Created', render: (u) => new Date(u.created_at).toLocaleDateString() },
                { key: 'actions', header: 'Actions', render: renderActions },
              ]}
              mobileCard={(u) => (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{u.username}</Typography>
                    {u.is_approved
                      ? <Chip label="Approved" color="success" size="small" />
                      : <Chip label="Pending" color="warning" size="small" />}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{u.email}</Typography>
                  <Box sx={{ mt: 1 }}>{renderRoles(u)}</Box>
                  <Box sx={{ mt: 1 }}>
                    <Chip label={u.tenant_name || 'No Tenant'} color={u.tenant_id ? 'info' : 'default'} size="small" />
                  </Box>
                  <Box sx={{ mt: 1.5 }}>{renderActions(u)}</Box>
                </Box>
              )}
            />
          );
        })()}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dialogAction === 'approve'
            ? 'Approve User'
            : dialogAction === 'unblock'
            ? 'Unblock User'
            : 'Block User'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogAction === 'approve'
              ? `Are you sure you want to approve ${selectedUser?.username}?`
              : dialogAction === 'unblock'
              ? `Are you sure you want to unblock ${selectedUser?.username}?`
              : `Are you sure you want to block ${selectedUser?.username}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmAction}
            color={dialogAction === 'block' ? 'error' : 'success'}
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog
        open={openInviteDialog}
        onClose={() => { setOpenInviteDialog(false); setInviteError(''); }}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent>
          {inviteError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{inviteError}</Alert>}
          <TextField
            margin="normal"
            required
            fullWidth
            label="Email Address"
            type="email"
            value={inviteData.email}
            onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
            disabled={inviteLoading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Username"
            value={inviteData.username}
            onChange={(e) => setInviteData({ ...inviteData, username: e.target.value })}
            disabled={inviteLoading}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={inviteData.role}
              onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
              label="Role"
              disabled={inviteLoading}
            >
              {manageableRoles.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {isSuperAdmin() && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Tenant</InputLabel>
              <Select
                value={inviteData.tenant_id}
                onChange={(e) => setInviteData({ ...inviteData, tenant_id: e.target.value })}
                label="Tenant"
                disabled={inviteLoading}
              >
                <MenuItem value=""><em>No Tenant</em></MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>{tenant.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenInviteDialog(false); setInviteError(''); }} disabled={inviteLoading}>
            Cancel
          </Button>
          <Button onClick={handleInviteSubmit} variant="contained" disabled={inviteLoading}>
            {inviteLoading ? <CircularProgress size={24} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Role (promote/demote) Dialog */}
      <Dialog
        open={Boolean(roleDialogUser)}
        onClose={() => setRoleDialogUser(null)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Change role</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Set the role for <strong>{roleDialogUser?.username}</strong>. This
            replaces their current role within your authority.
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={roleDialogValue}
              onChange={(e) => setRoleDialogValue(e.target.value)}
              label="Role"
              disabled={roleSaving}
            >
              {manageableRoles.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogUser(null)} disabled={roleSaving}>Cancel</Button>
          <Button onClick={handleSaveRole} color="primary" variant="contained" disabled={roleSaving}>
            {roleSaving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit User Tenant
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Change the tenant association for {editingUser?.username}
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Tenant</InputLabel>
            <Select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              label="Tenant"
            >
              <MenuItem value="">
                <em>No Tenant</em>
              </MenuItem>
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            onClick={handleSaveTenantChange}
            color="primary"
            variant="contained"
            autoFocus
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Reset Password Confirm Dialog */}
      <Dialog
        open={Boolean(resetConfirmUser)}
        onClose={() => setResetConfirmUser(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send password reset link?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This emails <strong>{resetConfirmUser?.email}</strong> a branded
            password-reset link (valid for 60 minutes). They click it and choose
            their own new password — the same flow as self-service
            <em> Forgot password</em>. No temporary passwords or verification
            codes. Use this whenever a user is locked out or has lost access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirmUser(null)}>Cancel</Button>
          <Button onClick={handleResetPasswordConfirm} color="warning" variant="contained">
            Send reset link
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement;
